import React, { useCallback, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { usePDF } from '@react-pdf/renderer'
// evertel
import { useContainer } from '@evertel/di'
import { Icon, FloatingMenu, MenuItem, MenuDivider, useUI } from '@evertel/web/ui'
import { APIDataDocument, APIDataDocumentSchema } from '@evertel/types'
import { DocumentPDF } from '../DocumentPDF'


interface MenuItemsProps {
    document: APIDataDocument
    userId: number
    schema: APIDataDocumentSchema
    canEdit: boolean
    canShare: boolean
    canClone: boolean
    onEdit?: (documentId: number) => void
    onShare?: (documentId: number) => void
    onClone: () => void
    onDelete?: (documentId: number) => void
    onExportAsCSV: () => void
    onToggleArchive: () => void
    onTrackPdfDownload?: () => void
    attachId?: string
}

const DocumentMenu: React.FC<MenuItemsProps> = observer(({
    document,
    userId,
    schema,
    canEdit,
    canShare,
    canClone,
    onEdit,
    onShare,
    onClone,
    onDelete,
    onExportAsCSV,
    onToggleArchive,
    onTrackPdfDownload,
    attachId
}) => {

    const container = useContainer()
    const { isConfirmed } = useUI()

    const [pdf, updatePdf] = usePDF(
        { document:
            <DocumentPDF
                document={document}
                schema={schema}
                user={userId}
                value={document.data}
                container={container}
            />
        }
    )

    useEffect(() => {
        updatePdf(<DocumentPDF
            document={document}
            schema={schema}
            user={userId}
            value={document.data}
            container={container}
        />)
    }, [document, document.data, schema, userId])

    const canCloneConfirm = useCallback(async () => {
        const confirmed = await isConfirmed({
            title: `Clone ${document?.name}?`,
            message: `A new document named 'Copy of ${document?.name}' will be created.`,
            acceptButton: {
                label: 'Clone',
                color: 'primary'
            },
            cancelButton: {
                label: 'Cancel',
                color: 'link'
            }
        })

        if (!confirmed) return

        if (onClone) onClone()

    }, [])

    const handlePdfDownload = useCallback(() => {
        if (onTrackPdfDownload) {
            onTrackPdfDownload()
        }
    }, [onTrackPdfDownload])

    return (
        <FloatingMenu
            attachId={attachId}
            label="Document Actions"
            renderLabel={() => (
                <span className="text-nowrap">
                    <Icon
                        name="file-pen"
                        className="pe-2"
                    />
                    <Icon
                        type="solid"
                        name="caret-down"
                    />
                </span>
            )}
            className="px-3"
        >
            {onEdit && (
                <MenuItem
                    label="Edit"
                    icon={{
                        name: 'edit',
                        className: 'me-1',
                        color: (!canEdit || document?.isArchived) ? 'muted' : 'body-color'
                    }}
                    onClick={canEdit ? () => onEdit(document?.id) : undefined}
                    disabled={!canEdit || document?.isArchived}
                    className={(!canEdit) ? 'text-muted' : undefined}
                />
            )}
            {onShare && (
                <MenuItem
                    label="Share"
                    icon={{
                        name: 'share',
                        className: 'me-1',
                        color: (!canShare) ? 'muted' : 'body-color'
                    }}
                    onClick={(canShare && !document?.isArchived) ? ( () => onShare(document?.id) ) : undefined}
                    disabled={(!canShare || document?.isArchived)}
                    className={(!canShare || document?.isArchived) ? 'text-muted' : undefined}
                />
            )}
            <MenuItem
                label="Clone"
                icon={{
                    name: 'clone',
                    className: 'me-1'
                }}
                disabled={!canClone}
                onClick={canCloneConfirm}
            />
            <MenuItem
                label="Export as PDF"
                icon={{
                    name: 'file-pdf',
                    className: 'me-1'
                }}
                href={pdf.url}
                download={`${document?.name || 'EverDoc'}-${schema.name}.pdf`}
                onClick={handlePdfDownload}
            />
            <MenuItem
                label="Export as CSV"
                icon={{
                    name: 'file-csv',
                    className: 'me-1'
                }}
                onClick={onExportAsCSV}
            />
            <MenuDivider />
            <MenuItem
                label={document?.isArchived ? 'Unarchive' : 'Archive'}
                icon={{
                    name: 'box-archive',
                    className: 'me-1',
                    color: (!canEdit) ? 'muted' : undefined
                }}
                onClick={onToggleArchive}
                disabled={!canEdit}
                className={(!canEdit) ? 'text-muted' : 'text-danger'}
            />
            {!!canEdit && !!onDelete && (
                <MenuItem
                    label="Delete Document"
                    icon={{
                        name: 'trash',
                        className: 'me-1',
                        color: 'danger'
                    }}
                    onClick={() => onDelete(document?.id)}
                    className="text-danger"
                />
            )}
        </FloatingMenu>

    )
})

export { DocumentMenu }
DocumentMenu.displayName = 'DocumentMenu (ButtonDropdown)'
